import * as React from 'react'
import {ReactElement} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop, DisplayItemLabelDiv} from '../../../../styledComponents/DisplayItem'
import {StyledSecondaryButton} from '../../../../styledComponents/Button'
import {Box} from '@mui/material'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {RESIDENT_APPLY_STATUS} from '../../../../CodeMaster'
import {NatResidentApplicationHistoryInfoComponent} from '../common/resident/NatResidentApplicationHistoryInfoComponent'
import styled from 'styled-components'
import {NatPetBreedingDetail} from '../repository/NatGetPetBreeding'
import {FullWidthColumnFlexContainer, RowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatGetApplicationResultDetail} from '../repository/NatGetApplicationResult'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import NatResidentPetBreedingApplyConfirm from './resident/NatResidentPetBreedingApplyConfirm'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {ImageInformation} from '../../../../components/shared/forms/resident/PPImageInputWithPreviewForResident'
import {convertApplyStatusIntoApplyHistoryDivision} from '../common/helper/NatApplicationHelper'
import {Colors} from '../../../../styledComponents/Constants'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId: string
    applicationRepo: NatApplicationRepo
    location: Location
}

interface States {
    petBreedingDetail: NatPetBreedingDetail
    applicationResultDetail: NatGetApplicationResultDetail
    isLoadCompleted: boolean
}

class NatResidentPetBreedingPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            petBreedingDetail: {
                applyId: '',
                applyStatus: '',
                condominiumCd: '',
                condominiumName: '',
                ridgeName: '',
                roomNumber: '',
                applicantFamilyName: '',
                applicantFirstName: '',
                applyDate: '',
                petType: '',
                petBreed: '',
                havePetStartMonth: '',
                healthCenterDogLicense: {
                    fileNumber: 0,
                    path: '',
                },
                rabiesPreventionInjectionCertification: {
                    fileNumber: 0,
                    path: '',
                },
                version: 0,
            },
            applicationResultDetail: {
                applyInfo: {
                    applyId: '',
                    applyCategory: '',
                    ridgeName: '',
                    roomNumber: '',
                    familyName: '',
                    firstName: '',
                    registerDatetime: '',
                },
                resultInfo: {
                    applyStatus: '',
                    roleId: '',
                    comment: '',
                    registerDatetime: '',
                },
            },
            isLoadCompleted: false,
        }

        this.onClickReApplyButton = this.onClickReApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        const [petBreedingResponse, applicationStatusResponse] = await Promise.all([
            this.getPetBreeding(),
            this.getApplicationResult(),
        ])

        if (petBreedingResponse && applicationStatusResponse) {
            this.setState({
                petBreedingDetail: petBreedingResponse,
                applicationResultDetail: applicationStatusResponse,
            })
        }
    }

    async getPetBreeding(): Promise<Nullable<NatPetBreedingDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getPetBreedingForResident.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    async getApplicationResult(): Promise<Nullable<NatGetApplicationResultDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getApplyResult.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    onClickReApplyButton(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/applications/petBreeding/${this.props.applyId}/copy`,
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        const previewHealthCenterDogLicense: ImageInformation = {
            fileName: null,
            fileData: null,
            fileNumber: this.state.petBreedingDetail.healthCenterDogLicense.fileNumber,
            path: this.state.petBreedingDetail.healthCenterDogLicense.path,
        }

        const previewRabiesPreventionInjectionCertification: ImageInformation = {
            fileName: null,
            fileData: null,
            fileNumber: this.state.petBreedingDetail.rabiesPreventionInjectionCertification.fileNumber,
            path: this.state.petBreedingDetail.rabiesPreventionInjectionCertification.path,
        }

        return (
            <>
                <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/applications/history`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentApplicationHistoryInfoComponent
                    applyId={this.props.applyId}
                    applyHistoryDivision={
                        convertApplyStatusIntoApplyHistoryDivision(this.state.petBreedingDetail.applyStatus)}
                    applyResultInfo={this.state.applicationResultDetail}
                />
                <NatResidentPetBreedingApplyConfirm
                    {...this.state.petBreedingDetail}
                    healthCenterDogLicense={previewHealthCenterDogLicense}
                    rabiesPreventionInjectionCertification={previewRabiesPreventionInjectionCertification}
                    applyFlag={false}
                />
                <BlockSpacer height={'5px'}/>
                <ItemDiv>
                    <GridContainer alignItems={'flex-start'}>
                        <GridItem over={3}>
                            <DisplayItemLabelDiv>
                                <Box mr={'5px'}>
                                    飼育承認条件
                                </Box>
                            </DisplayItemLabelDiv>
                        </GridItem>
                        <GridItem over={9}>
                            <RowFlexContainer>
                                <Box whiteSpace={'pre-wrap'}>
                                    一 管理規約、使用規則並びにペット飼育規則を遵守すること<br/>
                                    二 飼育を中止する場合には、管理組合まで通知すること
                                </Box>
                            </RowFlexContainer>
                        </GridItem>
                    </GridContainer>
                </ItemDiv>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.APPLY_PET_BREEDING_APPLICATION}>
                    {(this.state.petBreedingDetail.applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value ||
                            this.state.petBreedingDetail.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) &&
                        <ReApplyButton
                            onClick={this.onClickReApplyButton}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    内容を編集して再申請する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </ReApplyButton>
                    }
                </AuthorityChecker>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentPetBreedingPage)

export const ReApplyButton = styled(StyledSecondaryButton)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 15px;
`

const ItemDiv = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    margin: 0px -15px;
  `}
  padding: 20px 15px 20px 15px;
`
